if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.activeFetchId = 0;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.activeFetchId += 1;
        this.classList.remove('quick-add-modal--loading');
        this.modalContent.removeAttribute('aria-busy');
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');
        const fetchId = (this.activeFetchId += 1);
        this.classList.add('quick-add-modal--loading');
        this.modalContent.setAttribute('aria-busy', 'true');
        this.modalContent.innerHTML = this.getLoadingMarkup();
        super.show(opener);

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            if (fetchId !== this.activeFetchId) return;
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement) throw new Error('Product info was not found in quick add response.');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();
          })
          .catch(() => {
            if (fetchId !== this.activeFetchId) return;
            this.modalContent.innerHTML = `
              <div class="quick-add-modal__error" role="alert">
                ${window.accessibilityStrings?.quickAddError || 'Product details could not be loaded.'}
              </div>
            `;
          })
          .finally(() => {
            if (fetchId === this.activeFetchId) {
              this.classList.remove('quick-add-modal--loading');
              this.modalContent.removeAttribute('aria-busy');
            }
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      getLoadingMarkup() {
        return `
          <div class="quick-add-modal__loading" role="status" aria-live="polite">
            <div class="quick-add-modal__loading-spinner">
              <svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
              </svg>
            </div>
            <div class="quick-add-modal__skeleton" aria-hidden="true">
              <span class="quick-add-modal__skeleton-media"></span>
              <div class="quick-add-modal__skeleton-info">
                <div class="quick-add-modal__skeleton-copy">
                  <div class="quick-add-modal__skeleton-title">
                    <span class="quick-add-modal__skeleton-line quick-add-modal__skeleton-line--title"></span>
                    <span class="quick-add-modal__skeleton-line quick-add-modal__skeleton-line--title-short"></span>
                  </div>
                  <div class="quick-add-modal__skeleton-price-row">
                    <span class="quick-add-modal__skeleton-line quick-add-modal__skeleton-line--compare-price"></span>
                    <span class="quick-add-modal__skeleton-line quick-add-modal__skeleton-line--price"></span>
                  </div>
                  <div class="quick-add-modal__skeleton-option-group">
                    <span class="quick-add-modal__skeleton-line quick-add-modal__skeleton-line--option-label"></span>
                    <div class="quick-add-modal__skeleton-options quick-add-modal__skeleton-options--color">
                      <span class="quick-add-modal__skeleton-pill"></span>
                    </div>
                  </div>
                  <div class="quick-add-modal__skeleton-option-group">
                    <span class="quick-add-modal__skeleton-line quick-add-modal__skeleton-line--option-label quick-add-modal__skeleton-line--option-label-wide"></span>
                    <div class="quick-add-modal__skeleton-options quick-add-modal__skeleton-options--sizes">
                      <span class="quick-add-modal__skeleton-pill"></span>
                      <span class="quick-add-modal__skeleton-pill"></span>
                      <span class="quick-add-modal__skeleton-pill"></span>
                      <span class="quick-add-modal__skeleton-pill"></span>
                      <span class="quick-add-modal__skeleton-pill"></span>
                      <span class="quick-add-modal__skeleton-pill"></span>
                      <span class="quick-add-modal__skeleton-pill"></span>
                      <span class="quick-add-modal__skeleton-pill"></span>
                    </div>
                  </div>
                  <div class="quick-add-modal__skeleton-actions">
                    <span class="quick-add-modal__skeleton-quantity"></span>
                    <span class="quick-add-modal__skeleton-button">
                      <span class="quick-add-modal__skeleton-button-icon"></span>
                    </span>
                  </div>
                  <span class="quick-add-modal__skeleton-line quick-add-modal__skeleton-line--details"></span>
                </div>
              </div>
            </div>
          </div>
        `;
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
